import { getIncidentSeverityDefaultValue } from '@features/logs/utils/incidentSeverityUtils'
import type { Home, IncidentReportSettingsUpdateBody } from '@lmc/ts-common'
import {
  Add,
  Form,
  FormCard,
  FormItem,
  Input,
  PageContentWrapper,
  PageHeader,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import * as GlobalActions from 'actions/Global'
import { Form as AntdForm, Switch } from 'antd'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { handleOnFinishFailed } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import styled from 'styled-components'

const IncidentReportSettingsScreen = () => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = AntdForm.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const dispatch = useDispatch()

  const hasHomeLocations: boolean =
    home.features?.hasHomeLocations?.enabled || false

  const [showLocations, setShowLocations] = useState<boolean>(hasHomeLocations)

  const initialValues = {
    locations: home.locations,
    incidentSeverity: home.incidentSeverity,
    hasHomeLocations
  }

  const updateFunction = (values: IncidentReportSettingsUpdateBody) =>
    postJSON({
      url: `/list/homes/${home.id}/incident-report`,
      method: 'PUT',
      json: values
    })

  const { mutateAsync: updateIncidentSettings } = useMutation(
    ['incident-reports-settings', home.id],
    updateFunction,
    {
      onSuccess: (res) => {
        dispatch(GlobalActions.setHomeDetails(res.data))
        showToast({
          title: t(incidentReportsConstants.UPDATED_INCIDENT_REPORT_SETTINGS)
        })
      }
    }
  )

  const handleOnFinish = async (values: IncidentReportSettingsUpdateBody) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      await updateIncidentSettings(values)
      setIsUpdateLoading(false)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
      setIsUpdateLoading(false)
    }
  }

  const handleFormValuesChange = (values: IncidentReportSettingsUpdateBody) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const onEnableLocationsSwitchChange = (checked: boolean) => {
    if (checked) {
      setShowLocations(true)
    } else {
      setShowLocations(false)
    }
  }

  return (
    <Wrapper>
      <PageHeader
        sticky
        title={t(incidentReportsConstants.INCIDENT_REPORTING)}
        subtitle={t(incidentReportsConstants.INCIDENT_REPORTING_DESCRIPTION, {
          variable: home.preferredPeopleNominative
        })}
      >
        <StyledButton
          variant="primary"
          isLoading={isUpdateLoading}
          onClick={form.submit}
        >
          {t(incidentReportsConstants.SAVE_CHANGES)}
        </StyledButton>
      </PageHeader>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={handleFormValuesChange}
        onFinish={(values) => handleOnFinish(values)}
        onFinishFailed={({ errorFields }) => handleOnFinishFailed(errorFields)}
      >
        <FormCard
          title={
            <FormItem
              name="hasHomeLocations"
              label={
                <Text>{t(incidentReportsConstants.ENABLE_LOCATIONS)}</Text>
              }
            >
              <Switch
                onChange={onEnableLocationsSwitchChange}
                checked={showLocations}
              />
            </FormItem>
          }
        >
          {showLocations && (
            <AntdForm.List name="locations">
              {(fields, { add, remove }) => (
                <LocationsContainer>
                  {fields.map((field, index) => (
                    <LocationContainer
                      key={['locations-container', field.key].join('-')}
                    >
                      <FormItem
                        name={index}
                        key={['locations', field.key].join('-')}
                      >
                        <Input />
                      </FormItem>
                      <DeleteButton
                        variant="text"
                        onClick={() => remove(field.name)}
                      >
                        <Trash color={theme.color.neutral.color4} />
                      </DeleteButton>
                    </LocationContainer>
                  ))}
                  <Button type="button" variant="text" onClick={() => add()}>
                    <Add />
                    {t(incidentReportsConstants.ADD_LOCATION)}
                  </Button>
                </LocationsContainer>
              )}
            </AntdForm.List>
          )}
          <FormCard
            title={t(incidentReportsConstants.SEVERITY_LEVEL_NAMES)}
            subtitle={t(incidentReportsConstants.SEVERITY_LEVEL_DESCRIPTION)}
            level={2}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <FormItem
                name={['incidentSeverity', `${index + 1}`]}
                label={
                  <Text>
                    {t(incidentReportsConstants.LEVEL)} {index + 1}
                  </Text>
                }
                key={['incident-severity', index].join('-')}
              >
                <Input
                  maxLength={35}
                  placeholder={getIncidentSeverityDefaultValue(index + 1, t)}
                />
              </FormItem>
            ))}
          </FormCard>
        </FormCard>
      </Form>
      <NavigationModal canNavigate={!isDirty} />
    </Wrapper>
  )
}

const Wrapper = styled(PageContentWrapper).attrs({
  $paddingX: 24,
  $paddingBottom: 0
})`
  margin-bottom: 48px;

  & > *:first-child {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`

const LocationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
`

const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledButton = styled(Button)`
  white-space: nowrap;
`

const DeleteButton = styled(Button)`
  height: 24px;
`

export default IncidentReportSettingsScreen
